import { useEffect, useState } from "react";

const DISPLAY_INTERVAL_MS = 20;
// Reveal whole words rather than characters, and reveal more per tick the
// further behind we are. A steady stream keeps the backlog small (about a word
// per tick, so it reads like live typing), while a big provider burst or a long
// answer catches up fast instead of lagging behind. The tail always eases back
// down to one word per tick so the end still animates.
const MIN_WORDS_PER_TICK = 1;
const CATCH_UP_DIVISOR = 8;

const isWhitespace = (character: string) => /\s/.test(character);

/** Index (in code points) just past `wordCount` more whole words, including any
 * trailing whitespace so a word never renders detached from its spacing. */
function advanceWholeWords(
  characters: string[],
  from: number,
  wordCount: number,
): number {
  let index = from;
  for (let word = 0; word < wordCount && index < characters.length; word += 1) {
    while (index < characters.length && isWhitespace(characters[index])) {
      index += 1;
    }
    while (index < characters.length && !isWhitespace(characters[index])) {
      index += 1;
    }
  }
  while (index < characters.length && isWhitespace(characters[index])) {
    index += 1;
  }
  return index;
}

export default function useStreamingText(
  content: string,
  isStreaming: boolean,
) {
  const [displayedContent, setDisplayedContent] = useState(() =>
    isStreaming ? "" : content,
  );

  useEffect(() => {
    if (displayedContent === content) return;

    // A completed message may normalize its content before it reaches the
    // cache. Render that replacement immediately rather than animating a
    // string that is no longer a prefix of the target.
    if (!content.startsWith(displayedContent)) {
      setDisplayedContent(content);
      return;
    }

    const timeout = setTimeout(() => {
      // Work in code points so multi-unit characters (e.g. emoji) never split.
      const characters = Array.from(content);
      const shown = Array.from(displayedContent).length;
      const remaining = characters.slice(shown).join("").trim();
      const remainingWords = remaining ? remaining.split(/\s+/).length : 0;
      const wordsThisTick = Math.max(
        MIN_WORDS_PER_TICK,
        Math.ceil(remainingWords / CATCH_UP_DIVISOR),
      );
      setDisplayedContent(
        characters
          .slice(0, advanceWholeWords(characters, shown, wordsThisTick))
          .join(""),
      );
    }, DISPLAY_INTERVAL_MS);

    return () => clearTimeout(timeout);
  }, [content, displayedContent]);

  return {
    content: displayedContent,
    isAnimating: displayedContent !== content,
  };
}
